import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { LayoutProps } from './types';
import { getImageUrl } from './utils';

export const SlideshowLayout: React.FC<LayoutProps> = ({
  filteredItems,
  colors,
  data,
  onImageClick,
}) => {
  const [slideshowIndex, setSlideshowIndex] = useState(0);

  // Reset slideshow index when filtered items change
  useEffect(() => {
    if (slideshowIndex >= filteredItems.length) {
      setSlideshowIndex(0);
    }
  }, [filteredItems.length, slideshowIndex]);

  const currentItem = filteredItems[slideshowIndex];
  const mainImageUrl = currentItem ? getImageUrl(currentItem) : '';

  return (
    <div className="space-y-4">
      {/* Main Image Display */}
      {currentItem && (
        <div className="relative group">
          <div
            className={`w-full ${data.lightbox ? 'cursor-pointer' : ''}`}
            onClick={() => onImageClick(slideshowIndex)}
          >
            <div className="aspect-video overflow-hidden rounded-lg">
              {data.lazyLoading ? (
                <img
                  src={mainImageUrl}
                  alt={currentItem.alt || `Gallery image ${slideshowIndex + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <img
                  src={mainImageUrl}
                  alt={currentItem.alt || `Gallery image ${slideshowIndex + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              )}
            </div>
          </div>
          {data.captionPosition === 'below' && currentItem.caption && (
            <p className="mt-2 text-sm text-center" style={{ color: colors.captionColor }}>
              {currentItem.caption}
            </p>
          )}
          {filteredItems.length > 1 && (
            <>
              <button
                onClick={e => {
                  e.stopPropagation();
                  setSlideshowIndex(prev => (prev > 0 ? prev - 1 : filteredItems.length - 1));
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-opacity opacity-0 group-hover:opacity-100 z-10"
                aria-label="Previous image"
              >
                <ChevronLeft size={24} className="text-gray-700" />
              </button>
              <button
                onClick={e => {
                  e.stopPropagation();
                  setSlideshowIndex(prev => (prev < filteredItems.length - 1 ? prev + 1 : 0));
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-opacity opacity-0 group-hover:opacity-100 z-10"
                aria-label="Next image"
              >
                <ChevronRight size={24} className="text-gray-700" />
              </button>
            </>
          )}
        </div>
      )}

      {/* Thumbnail Strip */}
      {filteredItems.length > 1 && (
        <div className="flex justify-center gap-2 overflow-x-auto py-2 px-1">
          {filteredItems.map((item, index) => {
            const thumbUrl = getImageUrl(item);
            const isActive = index === slideshowIndex;
            return (
              <button
                key={item.id}
                onClick={() => setSlideshowIndex(index)}
                className={`flex-shrink-0 relative transition-all duration-200 ${
                  isActive ? 'scale-105' : 'opacity-60 hover:opacity-100'
                }`}
                style={
                  isActive
                    ? {
                        border: `3px solid ${colors.filterButtonActiveColor}`,
                        borderRadius: '0.25rem',
                      }
                    : undefined
                }
                aria-label={`View image ${index + 1}`}
              >
                <div className="w-20 h-20 md:w-24 md:h-24 overflow-hidden rounded">
                  {data.lazyLoading ? (
                    <img
                      src={thumbUrl}
                      alt={item.alt || `Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <img
                      src={thumbUrl}
                      alt={item.alt || `Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
